import { onMounted, reactive, ref, watch } from "vue";
import Swal from "sweetalert2";
import i18n from "@/i18n";

import dashboardService, {
  type DashboardMonthlyItem,
  type DashboardRecentSaleItem,
  type DashboardStatsResponse,
  type DashboardStockAlertItem,
  type DashboardTopCustomerItem,
  type DashboardTopProductItem,
} from "@/services/dashboard";
import warehouseService, { type Warehouse } from "@/services/warehouse";

type DashboardFilters = {
  warehouse_id: string;
  from_date: string;
  to_date: string;
};

const createInitialFilters = (): DashboardFilters => ({
  warehouse_id: "",
  from_date: "",
  to_date: "",
});

export function useDashboard() {
  const warehouses = ref<Warehouse[]>([]);
  const filters = reactive<DashboardFilters>(createInitialFilters());

  const stats = ref<DashboardStatsResponse>({});
  const topProducts = ref<DashboardTopProductItem[]>([]);
  const monthlyStats = ref<DashboardMonthlyItem[]>([]);
  const stockAlerts = ref<DashboardStockAlertItem[]>([]);
  const topCustomers = ref<DashboardTopCustomerItem[]>([]);
  const recentSales = ref<DashboardRecentSaleItem[]>([]);

  const statsLoading = ref(false);
  const topProductsLoading = ref(false);
  const monthlyLoading = ref(false);
  const stockAlertsLoading = ref(false);
  const topCustomersLoading = ref(false);
  const recentSalesLoading = ref(false);

  let filtersDebounce: ReturnType<typeof setTimeout> | null = null;

  const buildQuery = () => ({
    warehouse_id: filters.warehouse_id || undefined,
    from_date: filters.from_date || undefined,
    to_date: filters.to_date || undefined,
  });

  const fetchWarehouses = async () => {
    try {
      warehouses.value = await warehouseService.getWarehouses();
    } catch (error) {
      console.error("Error fetching dashboard warehouses:", error);
    }
  };

  const fetchDashboardData = async () => {
    statsLoading.value = true;
    topProductsLoading.value = true;
    monthlyLoading.value = true;
    stockAlertsLoading.value = true;
    topCustomersLoading.value = true;
    recentSalesLoading.value = true;

    try {
      const query = buildQuery();
      const [
        statsResponse,
        topProductsResponse,
        monthlyResponse,
        stockAlertsResponse,
        topCustomersResponse,
        recentSalesResponse,
      ] = await Promise.all([
        dashboardService.getDashboardStats(query),
        dashboardService.getTopSellingProducts({ ...query, limit: 5 }),
        dashboardService.getMonthlySalesPurchases(query),
        dashboardService.getStockAlerts({ ...query, limit: 5 }),
        dashboardService.getTopCustomers(query),
        dashboardService.getRecentSales({ ...query, limit: 5 }),
      ]);

      stats.value = statsResponse || {};
      topProducts.value = topProductsResponse || [];
      monthlyStats.value = monthlyResponse || [];
      stockAlerts.value = stockAlertsResponse || [];
      topCustomers.value = topCustomersResponse || [];
      recentSales.value = recentSalesResponse || [];
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      Swal.fire({
        icon: "error",
        title: i18n.global.t("dashboard_page.load_failed"),
        text: i18n.global.t("dashboard_page.unable_to_load_dashboard_data"),
      });
    } finally {
      statsLoading.value = false;
      topProductsLoading.value = false;
      monthlyLoading.value = false;
      stockAlertsLoading.value = false;
      topCustomersLoading.value = false;
      recentSalesLoading.value = false;
    }
  };

  const refreshRecentSales = async () => {
    recentSalesLoading.value = true;
    try {
      recentSales.value = await dashboardService.getRecentSales({
        ...buildQuery(),
        limit: 5,
      });
    } catch (error) {
      console.error("Error refreshing recent sales:", error);
    } finally {
      recentSalesLoading.value = false;
    }
  };

  watch(
    filters,
    () => {
      if (filtersDebounce) {
        clearTimeout(filtersDebounce);
      }

      filtersDebounce = setTimeout(() => {
        fetchDashboardData();
      }, 250);
    },
    { deep: true }
  );

  onMounted(async () => {
    await fetchWarehouses();
    await fetchDashboardData();
  });

  return {
    warehouses,
    filters,
    stats,
    topProducts,
    monthlyStats,
    stockAlerts,
    topCustomers,
    recentSales,
    statsLoading,
    topProductsLoading,
    monthlyLoading,
    stockAlertsLoading,
    topCustomersLoading,
    recentSalesLoading,
    refreshRecentSales,
  };
}
